import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Filter } from '../../../../@types/filter';
import { recruitmentTypeApi } from '../../../../apis';
import { CreateRecruitmentTypeDto, RecruitmentType } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import InputWrap from '../../../../components/InputWrap';
import ModalWrap from '../../../../components/ModalWrap';
import SvgAdd from '../../../../components/svg/SvgAdd';
import SvgDelete from '../../../../components/svg/SvgDelete';
import SvgEdit from '../../../../components/svg/SvgEdit';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';

const StaffTypeManagement = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<RecruitmentType | null>(null);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });

  const {
    data: dataUserType,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY.USER_TYPE_LIST, filter],
    queryFn: () => {
      return recruitmentTypeApi.recruitmentTypeControllerGetAll(
        filter.page,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
        undefined,
      );
    },
  });

  const addMutation = useMutation({
    mutationFn: (payload: CreateRecruitmentTypeDto) => recruitmentTypeApi.recruitmentTypeControllerCreate(payload),
    onSuccess: () => {
      setOpenAddModal(false);
      refetch();
      form.resetFields();
      ToastSuccess({
        content: intl.formatMessage({ id: 'staffTypeManagement.addSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'staffTypeManagement.beenCreated' }),
        });
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateRecruitmentTypeDto }) =>
      recruitmentTypeApi.recruitmentTypeControllerUpdate(id, payload),
    onSuccess: () => {
      setOpenEditModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'staffTypeManagement.editSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'staffTypeManagement.beenCreated' }),
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => recruitmentTypeApi.recruitmentTypeControllerDelete(id),
    onSuccess: () => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        page: 1,
      }));
      setOpenDeleteModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'staffTypeManagement.deleteSuccees' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'staffTypeManagement.deleteFail' }),
      });
    },
  });

  const handleAddUserType = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreateRecruitmentTypeDto = {
          type: form.getFieldValue('addStaffType'),
        };
        addMutation.mutate(payload);
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleEditUserType = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreateRecruitmentTypeDto = {
          type: form.getFieldValue('editStaffType'),
        };
        editMutation.mutate({ id: selectedRowData?.id as string, payload });
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleDeleteUserType = () => {
    deleteMutation.mutate(selectedRowData?.id as string);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'staffTypeManagement.title' }),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return (
          <div className="staff-type-management-content-name">
            <p className="staff-type-management-content-name-tool">{record?.type}</p>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (record, row) => (
        <div className="staff-type-management-content-data-options">
          <ButtonWrap
            className="staff-type-management-content-button-edit"
            onClick={() => {
              setOpenEditModal(true);
              setSelectedRowData(row);
              form.setFieldValue('editStaffType', row.type);
            }}
          >
            <SvgEdit /> {intl.formatMessage({ id: 'staffTypeManagement.editButton' })}
          </ButtonWrap>
          {!row?.isDefault && (
            <ButtonWrap
              className="staff-type-management-content-button-delete"
              onClick={() => {
                setOpenDeleteModal(true);
                setSelectedRowData(row);
              }}
              disabled={row?.isDefault}
            >
              <SvgDelete />
              {intl.formatMessage({ id: 'staffTypeManagement.deleteButton' })}
            </ButtonWrap>
          )}
        </div>
      ),
    },
  ];
  return (
    <div className="staff-type-management">
      <div className="staff-type-management-content">
        <Widget
          breadcrumbs={[
            intl.formatMessage({ id: 'adminMenu.staffDepartmentPosition' }),
            intl.formatMessage({ id: 'staffTypeManagement.title' }),
          ]}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              paginationProps={{
                current: dataUserType?.data?.data?.page || 1,
                pageSize: dataUserType?.data?.data?.size || 20,
                total: dataUserType?.data?.data?.total || 0,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
              inputProps={{
                placeholder: intl.formatMessage({ id: 'staffTypeManagement.searchBox' }),
              }}
              tableProps={{
                dataSource: Array.isArray(dataUserType?.data?.data?.content) ? dataUserType?.data?.data?.content : [],
                columns,
                className: 'staff-type-management-content-table',
              }}
              rightTopContent={
                <>
                  <ButtonWrap
                    onClick={() => {
                      setOpenAddModal(true);
                    }}
                    variant="outlined"
                    className="staff-type-management-content-button-add"
                  >
                    <SvgAdd /> {intl.formatMessage({ id: 'staffTypeManagement.addButton' })}
                  </ButtonWrap>
                </>
              }
            />
          }
          title={intl.formatMessage({ id: 'staffTypeManagement.title' })}
        />
      </div>
      <div className="staff-type-management-content-modal">
        {openAddModal && (
          <ModalWrap
            open={openAddModal}
            cancelText={intl.formatMessage({ id: 'staffTypeManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'staffTypeManagement.addButton' })}
            buttonSubmitProps={{ disabled: addMutation.isPending }}
            onSubmit={handleAddUserType}
            onCancel={() => {
              setOpenAddModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'staffTypeManagement.addButtonTitle' })}
            className="staff-type-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="staff-type-management-content-title">
                  {intl.formatMessage({ id: 'staffTypeManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="addStaffType"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'staffTypeManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="staff-type-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'staffTypeManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="staff-type-management-content-modal">
        {openEditModal && (
          <ModalWrap
            open={openEditModal}
            cancelText={intl.formatMessage({ id: 'staffTypeManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'staffTypeManagement.editButton' })}
            buttonSubmitProps={{ disabled: editMutation.isPending }}
            onSubmit={handleEditUserType}
            onCancel={() => {
              setOpenEditModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'staffTypeManagement.editButtonTitle' })}
            className="staff-type-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="staff-type-management-content-title">
                  {intl.formatMessage({ id: 'staffTypeManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="editStaffType"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'staffTypeManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="staff-type-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'staffTypeManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="staff-type-management-content-modal">
        {openDeleteModal && (
          <ModalWrap
            open={openDeleteModal}
            cancelText={intl.formatMessage({ id: 'staffTypeManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'staffTypeManagement.deleteButton' })}
            buttonSubmitProps={{ disabled: deleteMutation.isPending }}
            onSubmit={handleDeleteUserType}
            onCancel={() => setOpenDeleteModal(false)}
            type="upsert"
            title={intl.formatMessage({ id: 'staffTypeManagement.deleteButtonTitle' })}
            className="staff-type-management-content-modal-delete"
            body={
              <div className="staff-type-management-content-modal-delete-body">
                <p className="staff-type-management-content-modal-delete-title">
                  <span className="staff-type-management-content-name-tool name">{selectedRowData?.type}</span>
                  {intl.formatMessage({ id: 'booking.modal.delete' })}
                </p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default StaffTypeManagement;
